import { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { graphqlRequestBaseQuery } from "@rtk-query/graphql-request-base-query";

import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import { isUnauthorizedError } from "~/services/graphqlErrorHelpers";
import intelligenceClient from "~/services/intelligenceClient";
import { buildGraphqlRequestContext, captureRtkQueryFailure } from "~/services/sentryRtkInstrumentation";

const graphqlBaseQuery = graphqlRequestBaseQuery({
	client: intelligenceClient
});

interface GraphQLRequestArgs {
	document: string | { toString(): string };
	variables?: Record<string, unknown> | void;
}

export const intelligenceBaseQuery: BaseQueryFn<GraphQLRequestArgs, unknown, unknown> = async (
	{ document, variables },
	api,
	extraOptions
) => {
	const startedAt = performance.now();
	const requestContext = buildGraphqlRequestContext(api, Config.intelligenceEndpoint);

	try {
		const token = await sfAuth.checkSession();

		if (!token) {
			return { data: undefined };
		}

		const result = await graphqlBaseQuery({ document: String(document), variables }, api, extraOptions);

		if (result.error && isUnauthorizedError(result.error)) {
			const refreshedToken = await sfAuth.checkSession(true);
			const resolution = await unauthorizedRetryPolicy.resolveUnauthorized(
				{ operationType: api.type, tokenRefreshed: !!refreshedToken },
				{
					redirectToLogin: () => sfAuth.redirectToLogin(),
					// async so the MaybePromise the transport returns is settled here, not by the helper.
					replay: async () => graphqlBaseQuery({ document: String(document), variables }, api, extraOptions)
				}
			);

			if (resolution.action === "redirectToLogin") {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: result.error,
					request: requestContext
				});
				return { error: result.error };
			}

			if (resolution.action === "surfaceNotReplayed") {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: resolution.notReplayedError,
					request: requestContext
				});
				return { error: resolution.notReplayedError };
			}

			const retryResult = resolution.replayed;
			if (retryResult.error) {
				captureRtkQueryFailure({
					api,
					durationMs: performance.now() - startedAt,
					error: retryResult.error,
					request: requestContext
				});
			}
			return retryResult;
		}

		if (result.error) {
			captureRtkQueryFailure({
				api,
				durationMs: performance.now() - startedAt,
				error: result.error,
				request: requestContext
			});
			return { error: result.error };
		}
		return result;
	} catch (e) {
		captureRtkQueryFailure({
			api,
			durationMs: performance.now() - startedAt,
			error: e,
			request: requestContext
		});
		return { error: e };
	}
};
